export type SocialPostImageAspect = "1:1" | "2:3";
export type SocialPostImageTextDensity = "minimal" | "rich";

export type SocialPostImagePromptInput = {
  industry: string;
  subject: string;
  additionalInfo: string;
  /** Si hay moodboard / referencias de línea gráfica adjuntas. */
  hasMarketingLineRefs?: boolean;
  /** Red objetivo de la publicación visual. */
  platform?: string;
  /** Nombre comercial opcional para branding genérico en la pieza. */
  brandName?: string;
  /** Formato de la pieza. */
  aspectRatio?: SocialPostImageAspect;
  /** Cantidad de texto integrado en el diseño. */
  textDensity?: SocialPostImageTextDensity;
};

const clean = (value: string) => value.trim().replace(/\s+/g, " ");

const aspectCopy = (aspect: SocialPostImageAspect) => {
  if (aspect === "2:3") {
    return {
      shape: "vertical 2:3 (retrato / feed vertical)",
      sizeHint: "composición vertical, útil para feed e historias-like",
      salida: "Una sola pieza gráfica final vertical 2:3",
    };
  }
  return {
    shape: "cuadrada 1:1",
    sizeHint: "formato feed clásico",
    salida: "Una sola pieza gráfica final cuadrada 1:1",
  };
};

const textDensitySections = (density: SocialPostImageTextDensity): string[] => {
  if (density === "rich") {
    return [
      "TEXTO EN LA IMAGEN (MÁS COMPLETO):",
      "- Incluí texto en español integrado en el diseño: titular + mensaje promocional/beneficio + CTA si corresponde.",
      "- Tipografía grande, legible y jerárquica (título > mensaje clave > detalle).",
      "- Podés usar porcentajes, beneficios o plazos si vienen en la info adicional.",
      "- El texto debe sentirse de agencia/marketing, no una pared ilegible.",
      "",
      "COMPOSICIÓN RECOMENDADA:",
      "- Layout de post promocional: titular + bloque de oferta/mensaje + apoyo visual del rubro.",
      "- Estética moderna, limpia y lista para feed; alta calidad, buen contraste.",
    ];
  }
  return [
    "TEXTO EN LA IMAGEN (MÍNIMO Y SIN REDUNDANCIA):",
    "- Máximo 1 titular corto + opcionalmente 1 línea de apoyo o CTA (en total preferí 2-6 palabras en el titular).",
    "- No satures la pieza con párrafos, listas, bullets, hashtags ni varios slogans a la vez.",
    "- No dupliques el mismo concepto (ej. titular + subtítulo + badge diciendo lo mismo).",
    "- Dejá que la imagen/ilustración comunique; el texto solo ancla el mensaje.",
    "- Tipografía grande, legible y con mucho aire; priorizá contraste y simplicidad.",
    "",
    "COMPOSICIÓN RECOMENDADA:",
    "- Layout limpio: visual dominante + poco texto bien jerarquizado.",
    "- Estética moderna, limpia y lista para feed; alta calidad, buen contraste.",
  ];
};

/**
 * Prompt para generar una pieza gráfica tipo post de redes (flyer/creativo),
 * no una foto suelta ni un mockup de la app de Instagram.
 */
export function buildSocialPostImagePrompt({
  industry,
  subject,
  additionalInfo,
  hasMarketingLineRefs = false,
  platform = "Instagram",
  brandName,
  aspectRatio = "1:1",
  textDensity = "minimal",
}: SocialPostImagePromptInput): string {
  const rubro = clean(industry) || "negocio local";
  const asunto = clean(subject) || "promoción";
  const extra = clean(additionalInfo);
  const marca = brandName ? clean(brandName) : "";
  const aspect = aspectCopy(aspectRatio);

  const sections = [
    `Genera una pieza gráfica ${aspect.shape} lista para publicar en ${platform}, con estilo de creativo profesional para redes sociales (diseño de agencia / marketing digital).`,
    `- Formato: ${aspect.sizeHint}.`,
    "",
    "CONTEXTO DEL FORMULARIO:",
    `- Industria o rubro: ${rubro}`,
    `- Asunto de la publicación: ${asunto}`,
    extra
      ? `- Información adicional: ${extra}`
      : "- Información adicional: reforzá el mensaje con un beneficio claro para la audiencia.",
    hasMarketingLineRefs
      ? "- Se adjuntan imágenes de línea de marketing (inspiración gráfica / moodboard)."
      : "",
    marca ? `- Marca o negocio (si aplica en el diseño): ${marca}` : "",
    "",
    "QUÉ DEBE COMUNICAR LA PIEZA:",
    textDensity === "rich"
      ? "- El asunto de la publicación debe verse como titular principal."
      : "- Un solo mensaje claro: el asunto como idea principal (no lo repitas con otras palabras).",
    textDensity === "rich"
      ? "- La información adicional debe convertirse en el mensaje promocional o beneficio central (descuento, plazo, servicio, evento, etc.)."
      : "- Si hay oferta o beneficio clave en la info adicional, resumilo en una frase corta o un dato fuerte (ej. un porcentaje).",
    "- El diseño debe sentirse coherente con la industria indicada (colores, iconografía, fotografía o ilustración de apoyo).",
    hasMarketingLineRefs
      ? "- Sigue la línea gráfica de las referencias adjuntas (colores, estilo, atmósfera) para que la pieza se sienta de la misma familia visual."
      : "",
    "",
    ...textDensitySections(textDensity),
    "",
    "EVITAR:",
    textDensity === "minimal"
      ? "- Abuso de texto, paredes de copy o más de dos bloques tipográficos."
      : "- Texto ilegible, deformado o amontonado sin jerarquía.",
    textDensity === "minimal"
      ? "- Frases redundantes, repetición del nombre/asunto o del beneficio en varios lugares."
      : "",
    "- Mockups de celular, capturas de Instagram, UI de la app (likes, comentarios, stories, barras del sistema).",
    "- Imagen solo fotográfica sin ningún mensaje claro.",
    "- Texto ilegible, deformado, en inglés salvo nombres propios, o lorem ipsum.",
    "- Logos reales de marcas famosas que no fueron provistas.",
    "- Nombres de mes, años sueltos de calendario, fechas de publicación o referencias tipo \"agosto\", \"septiembre 2026\", \"mes de…\", calendarios o sellos temporales del plan de contenidos. El mes de programación del post NO debe aparecer en la pieza.",
    "- Marcos, bordes decorativos, filetes, passepartout o bordes de color (azul, violeta, negro, blanco u otro) alrededor del diseño.",
    "- Fondos planos de relleno tipo marco: la pieza debe ir a sangre (full-bleed) hasta el borde del canvas.",
    "",
    "SALIDA:",
    `- ${aspect.salida}, sin marcos ni bordes decorativos, sin bordes de dispositivo ni marco de red social.`,
    textDensity === "minimal"
      ? "- Poco texto, sin redundancias, sin mes ni fecha de calendario (salvo vigencia de oferta pedida explícitamente en la info adicional)."
      : "- Sin mes ni fecha de calendario en el diseño, salvo que la info adicional pida explícitamente una fecha de vigencia de oferta.",
  ];

  return sections.filter(Boolean).join("\n");
}

/** OpenAI Images sizes allowed by the backend validator. */
export function openAiSizeForAspect(aspect: SocialPostImageAspect = "1:1"): string {
  // 1024x1536 = exactamente 2:3
  return aspect === "2:3" ? "1024x1536" : "1024x1024";
}
